// Intelligent Search API Service
import { getApiBaseUrl as getPermanentApiBaseUrl } from '../config/permanent-urls';
import { syncTokenBalance } from '../utils/tokenUpdate';
import { createInsufficientTokensError, handleInsufficientTokensError } from '../utils/insufficientTokensHandler';

const getApiBaseUrl = () => {
  return getPermanentApiBaseUrl();
};

const API_BASE_URL = getApiBaseUrl();

export interface IntelligentSearchRequest {
  query: string;
  context?: string; // Optional context (e.g., company name, job title)
  searchType?: 'job' | 'company' | 'general'; // Type of search
  sources?: string[]; // Optional array of domains to search (e.g., ['linkedin.com', 'indeed.com'])
  recencyDays?: number; // Optional recency filter in days (1 = past 24 hours, 7 = past week, etc.)
  topic?: 'news' | 'general'; // Tavily topic: 'news' for time-sensitive events, 'general' for evergreen content
  forceRefresh?: boolean; // Optional: bypass cache and charge tokens again
}

export interface IntelligentSearchResult {
  status?: 'processing';
  message?: string;
  results?: Array<{
    title: string;
    snippet: string;
    url: string;
    relevance?: number;
    fullContent?: string; // Full job description content from Tavily (for parsing)
  }>;
  citations?: string[];
  summary?: string;
  searchQuery?: string;
  tokensRemaining?: number;
  tokensUsed?: number;
}

class IntelligentSearchService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
    this.token = localStorage.getItem('token');
  }

  private createHeaders(extra?: HeadersInit): Headers {
    const headers = new Headers(extra);
    headers.set('Content-Type', 'application/json');
    
    // Always read from localStorage to get the latest token (after refresh)
    const tokenFromStorage = localStorage.getItem('token');
    if (tokenFromStorage) {
      this.token = tokenFromStorage; // Update cached token
      headers.set('Authorization', `Bearer ${tokenFromStorage}`);
    } else if (this.token) {
      // Fallback to cached token if localStorage is empty
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.createHeaders(options.headers);

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'omit',
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const safeErrorData = errorData && typeof errorData === 'object' ? errorData : {};
      
      const tokenError = createInsufficientTokensError(safeErrorData, response.status);
      if (tokenError) {
        handleInsufficientTokensError(tokenError);
        throw tokenError;
      }
      
      const errorMessage = safeErrorData.message || safeErrorData.detail || `HTTP ${response.status}: ${response.statusText || 'Unknown error'}`;
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).errorData = safeErrorData;
      throw error;
    }

    const data = await response.json();
    return data;
  }

  async search(request: IntelligentSearchRequest): Promise<IntelligentSearchResult> {
    const payload: Record<string, any> = { ...request };
    if (payload.forceRefresh !== undefined) {
      payload.force_refresh = payload.forceRefresh;
      delete payload.forceRefresh;
    }

    const response = await this.request<{ success: boolean; data: IntelligentSearchResult }>(
      '/intelligent-search',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    
    // Sync token balance from backend response (backend is source of truth)
    if (response.success !== false && response.data) {
      if (response.data.tokensRemaining !== undefined && response.data.tokensUsed !== undefined) {
        syncTokenBalance(response.data.tokensRemaining, response.data.tokensUsed);
      }
    }
    
    return response.data;
  }

  // Update token (called when user logs in)
  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }
}

const intelligentSearchServiceInstance = new IntelligentSearchService();

export const intelligentSearchService = intelligentSearchServiceInstance;

export default intelligentSearchServiceInstance;
